import { useState } from 'react';
import { showNotification, Notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { EventData, EventFormValues, User } from '../types';

interface UseEventSubmissionProps {
  form: any; // Mantine form instance
  event: EventData;
  editor: any; // TipTap editor instance
  user: User | null;
  croppedImage?: File | null;
}

export const useEventSubmission = ({ form, event, editor, user, croppedImage }: UseEventSubmissionProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const submitEvent = async (submitType: 'draft' | 'publish') => {
    console.log('🚀 Starting submission process with submitType:', submitType);
    setIsLoading(true);

    try {
      // 1) Determine URL + HTTP method
      const isEditing = Boolean(event?.id) || Boolean(form.values.id);
      const url = isEditing ? `/events/${event.slug}?no_redirect=true` : '/events';
      const method = isEditing ? 'PATCH' : 'POST';

      console.log('🔗 Request details:', { url, method, isEditing });

      // 2) Draft vs. publish validation
      if (submitType === 'draft') {
        if (!form.values.name?.trim()) {
          console.log('❌ Draft validation failed: no name');
          showNotification({
            title: 'Validation Error',
            message: 'Event name is required even for drafts.',
            color: 'red',
            icon: <IconAlertCircle size={16} />,
            autoClose: 5000,
          });
          setIsLoading(false); // ✅ Fixed: Reset loading state
          return;
        }
      } else {
        console.log('🔍 Running form validation for publish...');
        const validationErrors = form.validate();
        console.log('📊 Current form values:', {
          is_recurring: form.values.is_recurring,
          recurring_event_occurrences: form.values.recurring_event_occurrences,
          id: form.values.id,
          name: form.values.name,
          recurring_event_id: form.values.recurring_event_id,
          recurring_event: form.values.recurring_event,
          event_slug: form.values.slug,
        });
        if (validationErrors.hasErrors) {
          console.log('❌ Form validation failed:', validationErrors.errors);
          Notifications.clean();
          Object.entries(validationErrors.errors).forEach(([label, message], i) => {
            const pretty = label.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
            setTimeout(() => {
              Notifications.show({
                title: `Error with ${pretty}`,
                message: String(message),
                color: 'red',
                icon: <IconAlertCircle size={16} />,
                autoClose: 8000,
                position: 'top-right',
              });
            }, i * 200);
          });
          setIsLoading(false);
          return;
        }
        console.log('✅ Form validation passed');
      }

      // Helper to merge with existing event
      const mergeField = (field: keyof EventData): string => {
        const formValue = form.values[field as keyof typeof form.values];
        const eventValue = event && event[field as keyof typeof event];
        return (formValue as string) || (eventValue ? (eventValue as string) : '');
      };

      // 3) Timezone extraction with fallback
      let timezoneValue;
      if (Array.isArray(form.values.timezone)) {
        timezoneValue = form.values.timezone[0] || mergeField('timezone');
      } else {
        timezoneValue = form.values.timezone || mergeField('timezone');
      }

      if (!timezoneValue || timezoneValue.trim() === '') {
        timezoneValue = 'UTC';
        console.warn('🚨 Timezone was empty, using UTC as fallback');
      }

      // 4) Compute recurring_event_attributes
      if (form.values.is_recurring === 'true' && form.values.recurring_event_occurrences?.length > 0) {
        const first = form.values.recurring_event_occurrences[0];

        const attrs: Record<string, any> = {
          ...form.values.recurring_event_attributes,
          start_date: first.date,
          start_time: first.start_time,
          end_time: first.end_time,
          recurrence_pattern: form.values.recurring_event_attributes.recurrence_pattern,
          num_occurrences: form.values.recurring_event_occurrences.length,
          re_timezone: timezoneValue,
          spans_next_day: first.spans_next_day,
        };

        if (attrs.recurrence_pattern === 'Monthly') {
          const base = dayjs(first.date);
          const weekNum = Math.ceil(base.date() / 7);
          const ords = ['First', 'Second', 'Third', 'Fourth', 'Last'];
          attrs.ordinal_occurrence = ords[Math.min(weekNum - 1, 4)];
          attrs.occurrence_day = base.format('dddd');
        }

        form.setFieldValue('recurring_event_attributes', {
          ...form.values.recurring_event_attributes,
          ...attrs,
        });
      }

      // 5) Build FormData
      console.log('📋 Building FormData...');
      const formData = new FormData();
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

      // Submission flags
      if (submitType === 'draft') {
        formData.append('save_draft', 'true');
      } else {
        formData.append('submit_for_approval', 'true');
        if (isEditing) {
          formData.append('event[event_status_id]', '2');
        }
      }

      // Missing city information
      if (form.values.missing_city_detected) {
        formData.append('event[missing_city_detected]', 'true');
        formData.append('event[missing_city_name]', form.values.missing_city_name || '');
      } else {
        formData.append('event[missing_city_detected]', 'false');
      }

      // Date & time fields
      const sd = mergeField('event_start_date');
      const st = mergeField('event_start_time');
      const ed = mergeField('event_end_date');
      const et = mergeField('event_end_time');
      if (submitType === 'publish' || (sd && st)) {
        if (sd && st) formData.append('event[event_start_date]', `${sd.split('T')[0]}T${st}:00Z`);
        if (ed && et) formData.append('event[event_end_date]', `${ed.split('T')[0]}T${et}:00Z`);
      }

      formData.append('event[timezone]', timezoneValue);

      // Core scalar fields
      formData.append('event[name]', mergeField('name'));
      if (form.values.slug || event.slug) {
        formData.append('event[slug]', form.values.slug || event.slug || '');
      }
      formData.append('event[address]', mergeField('address'));

      if (form.values.recurring_event_id) {
        formData.append('event[recurring_event_id]', form.values.recurring_event_id.toString());
      }

      // Description
      if (editor?.getHTML) {
        formData.append('event[description]', editor.getHTML());
      }

      // Images
      if (form.values.event_image) {
        formData.append('event[event_image]', form.values.event_image);
      }
      if (croppedImage) {
        formData.append('event[cropped_image]', croppedImage);
      }

      // Optional URLs
      ['website_url', 'ticket_url', 'facebook_url', 'instagram_url', 'meetup_url', 'tiktok_url'].forEach((f) => {
        const v = form.values[f as keyof typeof form.values] || (event && event[f as keyof typeof event]) || '';
        if (v) formData.append(`event[${f}]`, v as string);
      });

      // Booleans
      formData.append('event[online_event]', form.values.online_event === 'true' ? 'true' : 'false');
      formData.append('event[is_recurring]', form.values.is_recurring === 'true' ? 'true' : 'false');
      formData.append('event[spans_next_day]', form.values.spans_next_day === 'true' ? 'true' : 'false');

      // Custom occurrences
      const finalOcc = form.values.recurring_event_occurrences || [];
      if (form.values.is_recurring === 'true' && finalOcc.length > 0) {
        formData.append('event[use_custom_occurrences]', 'true');
        finalOcc.forEach((occ: any, i: number) => {
          formData.append(`event[custom_occurrences][${i}][date]`, occ.date);
          formData.append(`event[custom_occurrences][${i}][start_time]`, occ.start_time);
          formData.append(`event[custom_occurrences][${i}][end_time]`, occ.end_time);
          formData.append(`event[custom_occurrences][${i}][spans_next_day]`, occ.spans_next_day.toString());
        });
      }

      // Arrays
      form.values.event_type_ids?.forEach((id: number) => formData.append('event[event_type_ids][]', id.toString()));
      form.values.dance_style_ids?.forEach((id: number) => formData.append('event[dance_style_ids][]', id.toString()));
      form.values.artistIds?.forEach((id: number) => formData.append('event[artist_ids][]', id.toString()));
      form.values.organizer_ids?.forEach((id: number) => formData.append('event[organizer_ids][]', id.toString()));

      // Recurring event attributes
      const isEditingExistingEvent = Boolean(event?.id);
      if (!isEditingExistingEvent) {
        const finalAttrs = form.values.recurring_event_attributes;
        Object.entries(finalAttrs).forEach(([key, val]) => {
          formData.append(`event[recurring_event_attributes][${key}]`, val != null ? val.toString() : '');
        });
      }

      // 6) Submit to API
      if (!csrfToken) throw new Error('CSRF token not found');

      // Add this right before the fetch call
      // const formDataEntries = [];
      // for (let [key, value] of formData.entries()) {
      //   formDataEntries.push(`${key}: ${value}`);
      // }

      // // This will show all form data and pause execution
      // alert('Form Data:\n' + formDataEntries.join('\n'));

      // // Or if you want to see specific values:
      // alert(
      //   `recurring_event_id: ${form.values.recurring_event_id}\nslug: ${form.values.slug}\nis_recurring: ${form.values.is_recurring}`,
      // );

      console.log('🚀 Making API request...');
      const response = await fetch(url, {
        method,
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        mode: 'same-origin',
        body: formData,
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
      });

      if (!response.ok) {
        console.log('❌ Response not OK, handling error...');
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await response.json();
          console.log('💥 Error data:', data);
          if (data.errors?.slug) {
            showNotification({
              title: 'Submission Failed',
              message: `Slug "${form.values.slug}" is already taken.`,
              color: 'red',
              icon: <IconAlertCircle size={16} />,
            });
          } else {
            showNotification({
              title: 'Submission Failed',
              message: Object.values(data.errors || data)
                .flat()
                .join(', '),
              color: 'red',
              icon: <IconAlertCircle size={16} />,
              position: 'top-right',
            });
          }
        } else {
          const text = await response.text();
          console.error('Server-side text error:', text);
          showNotification({
            title: 'Submission Failed',
            message: `Server error (${response.status}). See console for details.`,
            color: 'red',
            icon: <IconAlertCircle size={16} />,
            position: 'top-right',
          });
        }
        setIsLoading(false); // ✅ Fixed: Reset loading state on API errors
        return;
      }

      // Success handling
      console.log('✅ Request successful! Processing response...');
      let data: any = {};
      if (submitType !== 'draft') {
        try {
          data = await response.json();
          console.log('📦 Response data:', data);
        } catch (e) {
          console.log('⚠️ Could not parse response JSON:', e);
        }
      }

      const eventSlug = data.event?.slug || data.slug || form.values.slug;
      console.log('🐛 Debug redirect:', {
        'data.event?.slug': data.event?.slug,
        'form.values.slug': form.values.slug,
        eventSlug: eventSlug,
        submitType: submitType,
        is_recurring: form.values.is_recurring,
      });

      const pattern = form.values.recurring_event_attributes.recurrence_pattern || 'regular';
      const occurrences = form.values.recurring_event_attributes.num_occurrences;

      const successMessage =
        submitType === 'draft'
          ? 'Event draft was saved successfully!'
          : isEditingExistingEvent
            ? 'Event was updated successfully!'
            : form.values.is_recurring === 'true'
              ? `Successfully submitted ${pattern} recurring event with ${occurrences} occurrences!`
              : 'Event was submitted successfully!';

      console.log('🎉 About to show success notification...');
      try {
        showNotification({
          title: submitType === 'draft' ? 'Draft Saved' : 'Submission Successful',
          message: successMessage,
          color: 'green',
          icon: <IconCheck size={16} />,
          autoClose: 6000,
          position: 'top-right',
        });
        console.log('✅ Success notification shown');
      } catch (notificationError) {
        console.error('❌ Error showing notification:', notificationError);
      }

      // Redirect - Loading state is reset just before redirect
      const destination =
        submitType === 'draft' ? `/users/${user?.username}/managed-events?active_tab=draft` : `/events/${eventSlug}`;

      console.log('🧭 Calculated destination:', destination);
      console.log('🐛 Redirecting to:', destination);
      console.log('⏰ Setting timeout for redirect in 1.5 seconds...');
      setTimeout(() => {
        console.log('🚀 Executing redirect now to:', destination);
        setIsLoading(false); // ✅ Reset loading state before redirect
        window.location.href = destination;
      }, 1500);
    } catch (err: any) {
      console.error('💥 Network error:', err);
      showNotification({
        title: 'Submission Failed',
        message: 'Network error occurred. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
      setIsLoading(false); // ✅ Reset loading state on network errors
    }
  };

  return {
    submitEvent,
    isLoading,
  };
};
